import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { API_BASE } from "../api/config";
import "./OfficeTransferPlanningPanel.css";

// OFFICE_TRANSFER_PLANNING_REAL_UPLOAD_MANUAL_FINAL_V1

const COLUMNS = ["warehouse_code", "shop_code", "product_code", "color", "size", "brand", "qty", "note"];

const EMPTY_ROW = {
  warehouse_code: "DNGHT",
  shop_code: "GMP",
  product_code: "",
  color: "",
  size: "",
  brand: "HANGTEN",
  qty: 1,
  note: "",
};

function norm(v) {
  return String(v || "").trim().toUpperCase();
}

function txt(v) {
  return String(v || "").trim();
}

function normalizeRow(row) {
  return {
    warehouse_code: norm(row.warehouse_code || row.warehouseCode || row.warehouse),
    shop_code: norm(row.shop_code || row.shopCode || row.shop),
    product_code: norm(row.product_code || row.productCode || row.code),
    color: txt(row.color),
    size: txt(row.size),
    brand: norm(row.brand),
    qty: Number(row.qty || row.quantity || 0),
    note: txt(row.note),
  };
}

function validateRows(rows) {
  const errors = [];

  rows.forEach((row, index) => {
    const line = index + 1;
    if (!row.warehouse_code) errors.push(`Line ${line}: warehouse_code required`);
    if (!row.shop_code) errors.push(`Line ${line}: shop_code required`);
    if (!row.product_code) errors.push(`Line ${line}: product_code required`);
    if (!row.color) errors.push(`Line ${line}: color required`);
    if (!row.size) errors.push(`Line ${line}: size required`);
    if (!row.brand) errors.push(`Line ${line}: brand required`);
    if (Number(row.qty || 0) <= 0) errors.push(`Line ${line}: qty must be greater than 0`);
    if (row.shop_code === "AYM") errors.push(`Line ${line}: AYM is blocked`);
  });

  return errors;
}

function downloadTemplate() {
  const sample = [
    {
      warehouse_code: "DNGHT",
      shop_code: "GMP",
      product_code: "10463-031-031-03",
      color: "011",
      size: "L",
      brand: "HANGTEN",
      qty: 1,
      note: "Office plan sample",
    },
    {
      warehouse_code: "DNGHT",
      shop_code: "GMP",
      product_code: "10463-031-031-03",
      color: "201",
      size: "L",
      brand: "HANGTEN",
      qty: 1,
      note: "Office plan sample",
    },
  ];

  const ws = XLSX.utils.json_to_sheet(sample, { header: COLUMNS });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "office_transfer_plan");
  XLSX.writeFile(wb, "office_transfer_plan_template.xlsx");
}

export default function OfficeTransferPlanningPanel() {
  const [rows, setRows] = useState([]);
  const [manualRow, setManualRow] = useState(EMPTY_ROW);
  const [username, setUsername] = useState("office_admin");
  const [officeNote, setOfficeNote] = useState("");
  const [created, setCreated] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const errors = useMemo(() => validateRows(rows), [rows]);

  const summary = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const key = `${row.warehouse_code} → ${row.shop_code}`;
      const old = map.get(key) || { route: key, lines: 0, qty: 0 };
      old.lines += 1;
      old.qty += Number(row.qty || 0);
      map.set(key, old);
    });
    return Array.from(map.values());
  }, [rows]);

  function updateManual(field, value) {
    setManualRow((prev) => ({ ...prev, [field]: value }));
  }

  function addManualRow() {
    const row = normalizeRow(manualRow);
    const rowErrors = validateRows([row]);

    if (rowErrors.length > 0) {
      setError(rowErrors.join("\n"));
      return;
    }

    setRows((prev) => [...prev, row]);
    setManualRow({
      ...EMPTY_ROW,
      warehouse_code: row.warehouse_code,
      shop_code: row.shop_code,
      brand: row.brand,
    });
    setError("");
  }

  async function handleExcelFile(file) {
    if (!file) return;

    try {
      setError("");
      setMsg("");

      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const jsonRows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const normalized = jsonRows
        .map(normalizeRow)
        .filter((row) => row.warehouse_code || row.shop_code || row.product_code);

      setRows(normalized);
      setMsg(`${normalized.length} rows loaded from Excel.`);
    } catch (err) {
      setError(err.message || "Excel read failed");
    }
  }

  function removeRow(index) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  async function createPlan() {
    try {
      setLoading(true);
      setError("");
      setMsg("");
      setCreated([]);

      if (rows.length === 0) throw new Error("No rows to create.");
      if (errors.length > 0) throw new Error(errors.slice(0, 10).join("\n"));

      const res = await fetch(`${API_BASE}/api/erp/office-transfer-plan/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, officeNote, rows }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.errors?.join("\n") || data.message || "Create failed");
      }

      setCreated(Array.isArray(data.transfers) ? data.transfers : []);
      setMsg(`${data.transferCount || 0} transfer created. Batch: ${data.batchNo}`);
      setRows([]);
    } catch (err) {
      setError(err.message || "Create failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="officeTransferPlanningPage">
      <div className="officeTransferPlanningHero">
        <div>
          <div className="officeTransferPlanningTag">📦 OFFICE TRANSFER PLAN</div>
          <h1>Office Transfer Planning</h1>
          <p>
            Office မှ ဆိုင်အလိုက် ဘယ် code / color / size / qty ပို့မယ်ဆိုတာ သတ်မှတ်ပြီး
            Warehouse ကို transfer list ပို့ရန်။ Excel upload သို့မဟုတ် manual create ဖြင့် ပြုလုပ်နိုင်သည်။
          </p>
        </div>

        <button type="button" onClick={downloadTemplate}>
          Download Excel Template
        </button>
      </div>

      {msg && <div className="officePlanAlert officePlanAlert--success">{msg}</div>}
      {error && <div className="officePlanAlert officePlanAlert--error">{error}</div>}

      <section className="officePlanPanel">
        <h2>Excel Upload</h2>
        <p className="officePlanMuted">
          Columns: warehouse_code, shop_code, product_code, color, size, brand, qty, note
        </p>

        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => handleExcelFile(e.target.files?.[0])}
        />
      </section>

      <section className="officePlanPanel">
        <h2>Manual Add</h2>

        <div className="officePlanFormGrid">
          {COLUMNS.map((field) => (
            <label key={field}>
              <span>{field}</span>
              <input
                type={field === "qty" ? "number" : "text"}
                value={manualRow[field]}
                onChange={(e) => updateManual(field, e.target.value)}
              />
            </label>
          ))}

          <button type="button" onClick={addManualRow}>
            Add Row
          </button>
        </div>
      </section>

      <section className="officePlanPanel">
        <div className="officePlanSectionHead">
          <div>
            <h2>Preview Rows</h2>
            <p className="officePlanMuted">{rows.length} rows ready</p>
          </div>

          <div className="officePlanActionBox">
            <input value={username} onChange={(e) => setUsername(e.target.value)} />
            <input
              value={officeNote}
              onChange={(e) => setOfficeNote(e.target.value)}
              placeholder="Office note"
            />

            <button type="button" onClick={createPlan} disabled={loading || rows.length === 0}>
              {loading ? "Creating..." : "Create Transfer Plan"}
            </button>
          </div>
        </div>

        {summary.length > 0 && (
          <div className="officePlanSummary">
            {summary.map((item) => (
              <span key={item.route}>
                {item.route}: {item.lines} lines / {item.qty} qty
              </span>
            ))}
          </div>
        )}

        {errors.length > 0 && (
          <div className="officePlanAlert officePlanAlert--error">
            {errors.slice(0, 8).map((err) => (
              <div key={err}>{err}</div>
            ))}
            {errors.length > 8 && <div>...and {errors.length - 8} more errors</div>}
          </div>
        )}

        <div className="officePlanTableWrap">
          <table>
            <thead>
              <tr>
                {COLUMNS.map((col) => <th key={col}>{col}</th>)}
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="officePlanEmpty">
                    Excel upload သို့မဟုတ် Manual Add လုပ်ပါ။
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row.product_code}-${index}`}>
                    {COLUMNS.map((col) => <td key={col}>{row[col]}</td>)}
                    <td>
                      <button type="button" onClick={() => removeRow(index)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {created.length > 0 && (
        <section className="officePlanPanel">
          <h2>Created Transfers</h2>

          <div className="officePlanCreatedList">
            {created.map((row) => (
              <div key={row.id}>
                <b>{row.transferNo}</b>
                <span>{row.fromLocationCode} → {row.toLocationCode}</span>
                <span>Items: {row.itemCount}</span>
                <span>Total Qty: {row.totalQty}</span>
                <strong>{row.status}</strong>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
